import { useCallback, useContext, useMemo, useState } from "react";
import { ApiContext } from "../../../context/MyContext";
import { useUtils } from "../../../context/UtilsContext";
import { useCachedList } from "../../../hooks/useCachedList";
import {
  bookingRecordsCache,
  serializeBookingRecordsKey,
} from "../../../utils/listCache/caches";
import {
  fetchBookingRecords,
  modifyBookings,
} from "../../../service/bookingService";
import { resolveBookingEventTypeId } from "./bookingEventType";
import { statusIdFor } from "./bookingStatus";
import { planStatusWrite } from "./bookingStatusWrite";
import { bucketBookingsByLocalDate, monthRange } from "./slotGrid";

const EMPTY = [];

/**
 * Data layer for booking records — the dashboard's rows, the activity drawer's
 * history, and View slots' booked marks.
 *
 * Fetches a whole month at a time, keyed per (employee, month, scope), so every
 * reader of the same month shares one request: useCachedList dedupes
 * concurrent fetches by `namespace:key` and serves the cached month to
 * whichever mounts second.
 *
 * The fetch is deliberately *unfiltered* by employee. The endpoint's own scope
 * predicate already narrows to what this caller may see — their own bookings
 * plus every booking on a calendar owned by any of their employee records — so
 * one call returns both "my bookings" (the dashboard) and "bookings on my other
 * employee profile" (the conflict check). Passing `employee_id` would drop the
 * second set; see the note on `fetchBookingRecords`.
 *
 * `employeeId` alone retargets only the client-side split and the cache key —
 * never the request.
 *
 * `coachScoped` is the manager-reading-a-coach shape, and the one exception to
 * everything above: it *does* send `employee_id`, because the manager's own
 * unfiltered month contains none of that coach's rows and narrowing is the
 * whole point. The endpoint's scope predicate binds `actionable_employee_ids`
 * — owned **and** managed — so a manager reads the bookings of any coach who
 * reports to them directly, the same reporting line `/modify-coach-shift`
 * grants the edit through. Writing follows the same line: a manager may also
 * correct a booking on a direct report's calendar — `setStatus` below is what
 * does it.
 *
 * Rows arrive **one per booking** with its current status already resolved
 * server-side, and always with `status_history` (`includeHistory`), which is
 * what the client-activity drawer renders under each call.
 *
 * @param {{ year: number, month: number, employeeId?: string,
 *   coachScoped?: boolean, enabled?: boolean }} params - `month` is 0-indexed.
 *   `enabled: false` makes no request (the Shifts tab's week reads stay off
 *   until that tab is actually showing).
 */
export function useBookings({
  year,
  month,
  employeeId: targetEmployeeId,
  coachScoped = false,
  enabled = true,
}) {
  const { get, post, employeeData } = useContext(ApiContext);
  const { sessionUtils } = useUtils();

  // An EmployeeSchema.Employees id — NOT ApiContext.empId, which the OTP login
  // path sets to the UserProfile id.
  const employeeId = targetEmployeeId || employeeData?.data?.employee_id || null;
  // The same analytics event type the shift calls use; it is a label, never an
  // authorization input, so booking reads borrow it rather than register a new one.
  const eventTypeId = useMemo(
    () => resolveBookingEventTypeId(sessionUtils),
    [sessionUtils]
  );

  const configured = Boolean(employeeId && eventTypeId) && enabled;
  const cacheKey = configured
    ? serializeBookingRecordsKey(
        employeeId,
        year,
        month,
        coachScoped ? "coach" : "own"
      )
    : null;

  const fetcher = useCallback(() => {
    // monthRange: this route's bounds are instants, not
    // date-only strings resolved against UTC midnight.
    const { fromDate, toDate } = monthRange(year, month);
    return fetchBookingRecords(get, {
      eventTypeId,
      // Only in the coach-scoped shape; see the note above on why the caller's
      // own read stays unnarrowed.
      employeeId: coachScoped ? employeeId : undefined,
      fromDate,
      toDate,
      // One extra query server-side, and the drawer's whole content.
      includeHistory: true,
    }).then((payload) =>
      Array.isArray(payload?.booking_records) ? payload.booking_records : []
    );
  }, [get, eventTypeId, employeeId, coachScoped, year, month]);

  const { data, loading, isRevalidating, isRefreshing, error, refresh } =
    useCachedList({
      cache: bookingRecordsCache,
      key: cacheKey,
      fetcher,
      enabled: configured,
    });

  const current = Array.isArray(data) ? data : EMPTY;

  // Mine drive the dashboard; the rest are the caller's other employee
  // record's, kept in `bookingsByDate` for anything that needs both.
  const mine = useMemo(
    () =>
      current.filter(
        (record) => String(record?.coach_id) === String(employeeId)
      ),
    [current, employeeId]
  );

  const myBookingsByDate = useMemo(
    () => bucketBookingsByLocalDate(mine),
    [mine]
  );
  const bookingsByDate = useMemo(
    () => bucketBookingsByLocalDate(current),
    [current]
  );

  const [saving, setSaving] = useState(false);

  /**
   * Record what became of a call — one UPDATE by the booking's `id`.
   *
   * **Resolves as soon as the write does, and re-reads the month in the
   * background.** The caller is a modal that closes on this promise, and the
   * only question it is really asking is "did the server accept it?" — so
   * awaiting the re-read as well made the dialog sit there through a second
   * round trip (and more, once the workspace refreshed its other reads too), which
   * reads as a hang rather than as loading. The row behind updates a moment
   * later when the re-read lands; a badge that is briefly stale behind a closed
   * modal is a far smaller cost than a modal that will not close.
   *
   * The re-read runs on the failure path too, for the same reason
   * `confirmTimings` does: a refused write can still have left the month's rows
   * behind whatever the server now thinks, and a panel showing a status the API
   * has already disagreed with is worse than a brief flicker. It is never
   * awaited and never rejects — it records its own failure as a `RetryBanner` —
   * so only the write's error propagates to the caller, which is the one the
   * toast should show.
   *
   * @param {Object} params
   * @param {Object} params.booking - The row the action was raised from.
   * @param {string} params.targetToken - One of `STATUS`.
   * @param {Map<string, { id: string }>} params.statusIndex - From `useBookingStatuses`.
   * @param {string} [params.reason] - The note typed in the modal.
   * @returns {Promise<void>} Rejects with the API's own message on refusal.
   */
  const setStatus = useCallback(
    async ({ booking, targetToken, statusIndex, reason } = {}) => {
      const statusId = statusIdFor(statusIndex, targetToken);
      if (!statusId) {
        throw new Error(
          "That status isn't configured on this account. Sign out and back in, then try again."
        );
      }

      setSaving(true);
      try {
        await modifyBookings(post, {
          eventTypeId,
          bookings: [planStatusWrite({ booking, statusId, reason })],
        });
      } catch (err) {
        refresh();
        throw err;
      } finally {
        setSaving(false);
      }

      // Deliberately not awaited — see the docstring. The write has landed, the
      // caller can close, and this catches the panel up behind it.
      refresh();
    },
    [post, eventTypeId, refresh]
  );

  return {
    /**
     * The month's bookings as the API returned them, `start_time DESC`, each
     * with its `status_history` — what the activity drawer filters.
     */
    bookings: current,
    /** This employee record's, bucketed `YYYY-MM-DD` and ascending. */
    myBookingsByDate,
    /** All of them, bucketed — the conflict check needs the other profile's too. */
    bookingsByDate,
    employeeId,
    loading,
    busy: loading || isRevalidating || isRefreshing,
    // Whether the month's rows have actually arrived, so an empty day can be
    // told apart from one that simply hasn't loaded.
    hasMonthData: data !== undefined,
    error,
    refresh,
    hasEmployeeProfile: Boolean(employeeId),
    hasEventType: Boolean(eventTypeId),
    /** Record a call's outcome. See the docstring above. */
    setStatus,
    /** A status write is in flight — disables the modal's submit. */
    saving,
  };
}
